import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { TypedMemo } from '../common/types';
import { config } from '../../config/config';
import { useUserTokenDecoded } from '../../redux/slices/appInfo/hooks';
import { MobileOs, openAppUrl } from './mobileAppLinks';
import { StoreBadges } from './StoreBadges';
import { withBrandWord } from '../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole handoff; each part uses its own slice of it */
import styles from './MobileApps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PhoneAppHandoffProps {
  os: MobileOs;
  lead: string;
  next: string;
}

/**
 * Design 1d/d1: the handoff on a phone browser. A QR code is no use on the phone itself, so it
 * shows one button that opens the app, and the store badge for this phone only.
 */
const PhoneAppHandoffComponent: React.FC<PhoneAppHandoffProps> = ({ os, lead, next }) => {
  const intl = useIntl();
  const email = useUserTokenDecoded()?.email ?? '';
  const href = useMemo(() => openAppUrl(os, window.location.host, config.nativeAppScheme, next), [os, next]);

  return (
    <div className={styles.phone}>
      <p className={styles.lead}>{withBrandWord(lead)}</p>
      <a className="btn btn-primary btn-lg w-100" href={href}>
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-2" />
        {withBrandWord(
          intl.formatMessage({ id: 'app.sml.mobileApps.phone.open', defaultMessage: 'Open the MUSIC24 app' })
        )}
      </a>
      <div className={styles.or}>
        {intl.formatMessage({ id: 'app.sml.mobileApps.phone.noApp', defaultMessage: 'No app yet?' })}
      </div>
      <StoreBadges os={os} next={next} isCentered />
      <p className={styles.phoneNote}>
        {intl.formatMessage(
          { id: 'app.sml.mobileApps.phone.signIn', defaultMessage: 'Sign in to the app as {email}.' },
          { email }
        )}
      </p>
    </div>
  );
};

export const PhoneAppHandoff = TypedMemo(PhoneAppHandoffComponent);
